/**
 * The portal's half of the pin bridge (session 49, `.docs/49-mockup-pins-and-guests.md` §Pins;
 * session 55, `.docs/55-mockup-comments.md`).
 *
 * It listens to one frame — **only** that frame's window, because the mockup's own scripts can
 * post messages too — parses what it hears (`protocol.ts`), and tells the frame which pins to
 * draw on the page it is showing, which one is open, and whether a click there comments. A pin
 * on another page of the version moves the frame there first, and is focused once the bridge on
 * that page says it is ready. In return it learns where the open pin is on the screen, so the
 * thread can sit beside it, and which pins the page could draw at this width.
 *
 * Nothing a person wrote is ever sent: pins go as numbers and places, a text pin with its quote —
 * the page's own words.
 */

import type { MockupPin } from '@fareast/shared'
import { useCallback, useEffect, useRef, useState, type RefObject } from 'react'

import {
  FrameMessageSchema,
  PIN_TAG,
  PIN_VERSION,
  pageOf,
  pageUrlOf,
  type ParentMessage,
  type PinMark,
  type PinMode,
  type PinSpot,
} from './protocol.js'

/** A thread's pin as the bridge is told it: its number, where, and whether it is resolved. */
export type NumberedPin = {
  readonly n: number
  readonly pin: MockupPin
  readonly resolved: boolean
}

/** A point in the frame's own viewport, in CSS pixels. */
export type FramePoint = { readonly x: number; readonly y: number }

export type PinBridgeOptions = {
  readonly frame: RefObject<HTMLIFrameElement | null>
  /** Any address inside the version shown — the pins' pages are relative to its root. */
  readonly versionUrl: string
  /** The pins to draw — every page's; the bridge is sent only its own page's. */
  readonly pins: readonly NumberedPin[]
  readonly mode: PinMode
  /** Whether the person looking may comment: off, a selection and a right-click are the page's. */
  readonly gestures: boolean
  /** The spot the comment being written is about. */
  readonly draft: MockupPin | null
  /** The pin whose thread is open. */
  readonly active: number | null
  /** Somebody asked to comment on a spot of the page shown. */
  readonly onPlace: (pin: MockupPin) => void
  readonly onSelect: (n: number) => void
  /**
   * A key pressed in the frame — believed only while the frame has the keyboard, so a mockup's
   * own script cannot post Escape to throw away a comment being written in the portal.
   */
  readonly onKey: (key: 'escape' | 'comment') => void
}

export type PinBridge = {
  /** The page the frame is showing, or null before its bridge has spoken. */
  readonly page: string | null
  /** Brings pin `n` into view — on its own page, moving the frame there if it has to. */
  readonly focus: (n: number) => void
  /**
   * Where the draft's marker — else the open pin's — is in the frame's viewport, or null when it
   * is not on the page at this width (or nothing is open).
   */
  readonly where: FramePoint | null
  /** The pins the page shown could draw, at its width — the rest are elsewhere or hidden. */
  readonly shown: ReadonlySet<number>
}

export function usePinBridge(options: PinBridgeOptions): PinBridge {
  const { frame, versionUrl, pins, mode, gestures, draft, active } = options
  const [page, setPage] = useState<string | null>(null)
  // A second `ready` from the same page — a reload — has to be sent the state again.
  const [readyCount, setReadyCount] = useState(0)
  const [where, setWhere] = useState<FramePoint | null>(null)
  const [shown, setShown] = useState<ReadonlySet<number>>(NONE)
  const pendingFocus = useRef<number | null>(null)
  const handlers = useRef(options)
  handlers.current = options

  const send = useCallback(
    (message: DistributiveOmit<ParentMessage, 'fes' | 'v'>) => {
      // `*`: the frame's origin is opaque, so it is not one a message can be addressed to.
      frame.current?.contentWindow?.postMessage({ fes: PIN_TAG, v: PIN_VERSION, ...message }, '*')
    },
    [frame],
  )

  useEffect(() => {
    setPage(null)
    setWhere(null)
    setShown(NONE)
  }, [versionUrl])

  useEffect(() => {
    function onMessage(event: MessageEvent<unknown>) {
      const window = frame.current?.contentWindow
      if (window === null || window === undefined || event.source !== window) return
      const parsed = FrameMessageSchema.safeParse(event.data)
      if (!parsed.success) return
      const message = parsed.data
      const current = handlers.current
      switch (message.type) {
        case 'ready':
          setPage(pageOf(message.path, current.versionUrl))
          setReadyCount((count) => count + 1)
          setWhere(null)
          setShown(NONE)
          return
        case 'place': {
          // Only from somebody who may comment: the mockup's own scripts can post this too.
          if (!current.gestures) return
          const at = pageOf(message.path, current.versionUrl)
          if (at !== null) {
            current.onPlace({ page: at, ...message.spot, viewport_w: message.viewport_w })
          }
          return
        }
        case 'select':
          current.onSelect(message.n)
          return
        case 'key':
          if (document.activeElement === frame.current) current.onKey(message.key)
          return
        case 'where':
          setWhere(message.at)
          return
        case 'shown':
          setShown(new Set(message.ns))
          return
      }
    }
    window.addEventListener('message', onMessage)
    return () => {
      window.removeEventListener('message', onMessage)
    }
  }, [frame])

  // The frame's bridge announces itself; asking again on every load covers the other order.
  useEffect(() => {
    const element = frame.current
    if (element === null) return
    const hello = () => {
      send({ type: 'hello' })
    }
    element.addEventListener('load', hello)
    return () => {
      element.removeEventListener('load', hello)
    }
    // A new version is a new frame — `Preview` is keyed by it — and the listener goes with it.
  }, [frame, send, versionUrl])

  /*
   * What the frame is told, as one string — so it is told again only when that changes. A page
   * re-renders on every frame the page scrolls with a thread open (`where`), and a `state` makes
   * the bridge remake every pin and find every quote again.
   */
  const marks: PinMark[] =
    page === null
      ? []
      : pins
          .filter((one) => one.pin.page === page)
          .map((one) => ({ n: one.n, resolved: one.resolved, ...spotOf(one.pin) }))
  const told = JSON.stringify({
    type: 'state',
    pins: marks,
    mode,
    gestures,
    draft: draft !== null && draft.page === page ? spotOf(draft) : null,
    active,
  })
  useEffect(() => {
    if (page === null) return
    const state = JSON.parse(told) as Omit<Extract<ParentMessage, { type: 'state' }>, 'fes' | 'v'>
    send(state)
    const waiting = pendingFocus.current
    if (waiting !== null && state.pins.some((mark) => mark.n === waiting)) {
      pendingFocus.current = null
      send({ type: 'focus', n: waiting })
    }
  }, [page, readyCount, told, send])

  const focus = useCallback(
    (n: number) => {
      const target = handlers.current.pins.find((one) => one.n === n)
      if (target === undefined) return
      if (target.pin.page === page) {
        send({ type: 'focus', n })
        return
      }
      const address = pageUrlOf(handlers.current.versionUrl, target.pin.page)
      const element = frame.current
      if (address === null || element === null) return
      pendingFocus.current = n
      element.src = address
    },
    [frame, page, send],
  )

  return { page, focus, where, shown }
}

const NONE: ReadonlySet<number> = new Set()

/** A pin as the frame is told it: everything but its page and the width it was left at. */
export function spotOf(pin: MockupPin): PinSpot {
  const { page: _page, viewport_w: _width, ...spot } = pin
  return spot
}

type DistributiveOmit<T, K extends PropertyKey> = T extends unknown ? Omit<T, K> : never
